/**
 * The Finance dossier for one resident (moved out of FinanceShell in Tide
 * phase 2 so the Finance board can open it under a row). Same sections, same
 * sources, same acts: Record note, Request expedited dispense, and - new here
 * - Send back to UMED on an ACCEPTED handoff, the same signed act the
 * "Handed to Finance" list offers.
 */
import React, { useCallback, useEffect, useState } from 'react';
import {
  financeApi, outcomeOf, money, shortDate, freshKey, saleTermsLine, NOTE_KIND_LABEL, EXPEDITED_STATE_LABEL,
  FinanceScope, ResidentContext, NoteKind, Urgency, ExpeditedRequest, FinanceDocument, HandoffAmendment, AmendmentTerms,
} from './financeApi';
import { SendBackAction } from '../../components/sendBack/SendBackDialog';
import { sendBackPath } from '../../components/sendBack/sendBackApi';
import { APPROVAL_WORD_LABEL, STAGE_LABEL, approvalWordOf, stageLabelOf } from '../umed/umedApi';
import type { Stage } from '../umed/umedApi';

function Fact({ label, value, quiet, source }: { label: string; value: React.ReactNode; quiet?: boolean; source?: string | null }) {
  return (<><dt>{label}</dt><dd className={quiet ? 'none' : ''}>{value}{source ? <span className="fn-source"> · {source}</span> : null}</dd></>);
}

const HANDOFF_STATE_WORD: Record<string, string> = {
  RECEIVED: 'received', ACCEPTED: 'accepted', INVOICED: 'invoiced', TERMS_RECORDED: 'terms recorded', COLLECTING: 'collecting',
  PAID_IN_FULL: 'paid in full', CLOSED: 'closed', RETURNED_TO_UMED: 'returned to UMED',
};
const termsWords = (t: AmendmentTerms | null): string | null => (t && t.months !== null && t.monthlyCents !== null ? `${t.months} × ${money(t.monthlyCents / 100)}` : null);

/**
 * Ruling #1 (Isaiah Cotto, 2026-10-07): a correction or reversal completed after the handoff is an amendment ON the
 * one handoff, never a second one. Each says when (in Florida), what, who, and the terms before and after when known.
 */
function Amendments({ items }: { items: HandoffAmendment[] }) {
  if (!items.length) return null;
  return (
    <div className="fn-amendments" aria-label="Handoff amendments">
      <p>
        <span className="fn-corrected">Corrected {shortDate(items[0].corrected_on)}</span>
        {' '}{items.length === 1 ? 'One amendment' : `${items.length} amendments`} on the Finance handoff, not a second handoff.<span className="fn-source"> · eMerge</span>
      </p>
      <ul className="fn-notes">
        {items.map((a) => {
          const after = termsWords(a.terms ? a.terms.after : null);
          const before = termsWords(a.terms ? a.terms.before : null);
          return (
            <li key={a.id}>
              <strong>{shortDate(a.corrected_on)}</strong> · {a.summary}{a.amended_by_name ? ` · by ${a.amended_by_name}` : ''}
              {after && <> · terms now {after}{a.terms && a.terms.changed === true && before ? ` (was ${before})` : a.terms && a.terms.changed === false ? ' (unchanged)' : ''}</>}
              <span className="fn-source"> · {a.handoff_state ? `Finance had it ${HANDOFF_STATE_WORD[a.handoff_state] || a.handoff_state.toLowerCase()}` : 'before Finance received it'}</span>
            </li>
          );
        })}
      </ul>
    </div>
  );
}

function NoteForm({ patientId, kinds, onDone }: { patientId: string; kinds: NoteKind[]; onDone: (msg: string) => Promise<void> }) {
  const [kind, setKind] = useState<NoteKind>(kinds[0] || 'NOTE');
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  return (
    <form className="um-form" aria-label="Add a Finance note" onSubmit={async (e) => {
      e.preventDefault(); setBusy(true); setErr(null);
      try {
        const r = await financeApi.addNote(patientId, { kind, note: note.trim(), idempotency_key: freshKey() });
        if (!r.success || !r.data) { setErr(outcomeOf(r)); return; }
        setNote('');
        await onDone(r.data.replayed ? 'That note was already recorded.' : 'Note recorded.');
      } catch { setErr('Could not reach the server. Nothing was recorded.'); } finally { setBusy(false); }
    }}>
      <label><span>Kind</span>
        <select value={kind} onChange={(e) => setKind(e.target.value as NoteKind)} aria-label="Note kind">{kinds.map((k) => <option key={k} value={k}>{NOTE_KIND_LABEL[k]}</option>)}</select>
      </label>
      <label><span>Note</span><textarea value={note} onChange={(e) => setNote(e.target.value)} maxLength={4000} rows={3} aria-label="Finance note" /></label>
      <div className="btns"><button type="submit" className="um-open um-primary" disabled={busy || !note.trim()}>{busy ? 'Recording…' : 'Record note'}</button></div>
      {err && <p className="um-notice stop" role="alert">{err}</p>}
    </form>
  );
}

function ExpediteForm({ patientId, urgencies, onDone }: { patientId: string; urgencies: Urgency[]; onDone: (msg: string) => Promise<void> }) {
  const [reason, setReason] = useState('');
  const [summary, setSummary] = useState('');
  const [urgency, setUrgency] = useState<Urgency>('ASAP');
  const [byDate, setByDate] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  return (
    <form className="um-form" aria-label="Request an expedited dispense" onSubmit={async (e) => {
      e.preventDefault(); setBusy(true); setErr(null);
      try {
        const r = await financeApi.requestExpedited(patientId, { reason: reason.trim(), arrangement_summary: summary.trim() || undefined, urgency, requested_by_date: urgency === 'REQUESTED_BY_DATE' ? byDate : undefined, idempotency_key: freshKey() });
        if (!r.success || !r.data) { setErr(outcomeOf(r)); return; }
        await onDone(r.data.replayed ? 'That request is already with Scheduling.' : 'Request sent to Scheduling. Their answer will appear here.');
      } catch { setErr('Could not reach the server. Nothing was requested.'); } finally { setBusy(false); }
    }}>
      <label><span>Why expedite</span><textarea value={reason} onChange={(e) => setReason(e.target.value)} maxLength={2000} rows={2} aria-label="Expedite reason" /></label>
      <label><span>Arrangement (minimum necessary)</span><input value={summary} onChange={(e) => setSummary(e.target.value)} maxLength={1000} aria-label="Arrangement summary" placeholder="e.g. POA offered two payments" /></label>
      <label><span>Urgency</span>
        <select value={urgency} onChange={(e) => setUrgency(e.target.value as Urgency)} aria-label="Expedite urgency">
          {urgencies.map((u) => <option key={u} value={u}>{u === 'ASAP' ? 'As soon as possible' : 'By a date'}</option>)}
        </select>
      </label>
      {urgency === 'REQUESTED_BY_DATE' && <label><span>Requested by</span><input type="date" value={byDate} onChange={(e) => setByDate(e.target.value)} aria-label="Requested by date" /></label>}
      <div className="btns"><button type="submit" className="um-open um-primary" disabled={busy || !reason.trim()}>{busy ? 'Sending…' : 'Request expedited dispense'}</button></div>
      <p className="um-quiet">Scheduling decides and answers here. This does not change the schedule.</p>
      {err && <p className="um-notice stop" role="alert">{err}</p>}
    </form>
  );
}

function ExpeditedCard({ r }: { r: ExpeditedRequest }) {
  return (
    <div className="fn-request" aria-label="Expedited dispense request">
      <strong>{EXPEDITED_STATE_LABEL[r.state]}</strong>
      <span className="um-quiet"> · asked {shortDate(r.requested_at)}{r.requested_by_name ? ` by ${r.requested_by_name}` : ''}{r.urgency === 'REQUESTED_BY_DATE' && r.requested_by_date ? ` · by ${shortDate(r.requested_by_date)}` : ''}</span>
      <p>{r.reason}</p>
      {r.scheduling_response && <p className="um-quiet">Scheduling{r.scheduling_owner_name ? ` (${r.scheduling_owner_name})` : ''}: {r.scheduling_response}</p>}
      {r.scheduled_for && <p>Visit {shortDate(r.scheduled_for)}{r.visit_ref ? ` · ${r.visit_ref}` : ''}</p>}
    </div>
  );
}

function DocRow({ patientId, doc }: { patientId: string; doc: FinanceDocument }) {
  const [err, setErr] = useState<string | null>(null);
  const open = async () => {
    setErr(null);
    try {
      const r = await financeApi.documentUrl(patientId, doc.id);
      if (!r.success || !r.data) { setErr(outcomeOf(r)); return; }
      window.open(r.data.url, '_blank', 'noopener,noreferrer');
    } catch { setErr('Could not reach the server.'); }
  };
  const label = doc.document_class === 'BOM_VETTING' ? 'BOM vetting' : doc.document_class === 'RECORDING' ? 'Recording' : doc.document_class.replace(/_/g, ' ');
  return (
    <li><span>{label}{doc.original_filename ? ` · ${doc.original_filename}` : ''} · {shortDate(doc.source_created_at || doc.imported_at || doc.created_at)}</span>{' '}
      <button type="button" className="um-link" onClick={open}>Open</button>{err && <span className="um-notice stop" role="alert"> {err}</span>}</li>
  );
}

export function Context({ ctx, scope, notice, onChanged }: { ctx: ResidentContext; scope: FinanceScope; notice: string | null; onChanged: (msg: string) => Promise<void> }) {
  const a = ctx.account;
  const rp = ctx.responsible_party;
  const open = ctx.expedited_dispense.open;
  return (
    <section className="um-card" aria-label="Resident context">
      <div className="um-card-head">
        <div>
          <h2>{ctx.resident.name}</h2>
          <p className="why">{ctx.facility ? ctx.facility.name : 'No current building'}{ctx.resident.room ? ` · Room ${ctx.resident.room}` : ''}{ctx.resident.census_status ? ` · ${ctx.resident.census_status}` : ''}</p>
        </div>
      </div>
      {notice && <p className="um-notice ok" role="status">{notice}</p>}

      <div className="um-section">
        <h3>Current Finance work</h3>
        {open ? <ExpeditedCard r={open} /> : <p className="um-quiet">No expedited dispense request is open.</p>}
        {ctx.umed && ctx.umed.finance_state !== 'NOT_OPEN' && <p>UMED Finance state: <strong>{ctx.umed.finance_state.replace(/_/g, ' ').toLowerCase()}</strong></p>}
        {ctx.handoff && (
          <p>
            {ctx.handoff.kind === 'PRIVATE_PAY' ? 'Finance handoff (Private pay, no UMED)' : 'Finance handoff'}: <strong>{ctx.handoff.state.replace(/_/g, ' ').toLowerCase()}</strong><span className="fn-source"> · eMerge</span>
            {ctx.handoff.kind === 'PRIVATE_PAY' && saleTermsLine(ctx.handoff.sale) && <><br /><span className="fn-pp-terms">Sale: {saleTermsLine(ctx.handoff.sale)}</span></>}
          </p>
        )}
        {ctx.amendments && <Amendments items={ctx.amendments} />}
        {/* Only the person who accepted a handoff holds it, so only an ACCEPTED one is offered; the preview and the server decide again. */}
        {scope.can?.send_back && ctx.handoff && ctx.handoff.kind !== 'PRIVATE_PAY' && ctx.handoff.state === 'ACCEPTED' && (
          <div className="fn-sendback">
            <SendBackAction path={sendBackPath.finance(ctx.handoff.id)} recordLabel={ctx.resident.name} className="um-open" label="Send back to UMED…"
              onClosed={(done) => { if (done) onChanged('Sent back to UMED.'); }} />
          </div>
        )}
      </div>

      <div className="um-section">
        <h3>Balance, terms, aging</h3>
        <dl className="um-kv">
          <Fact label="Account status" value={a.status || 'No Finance account'} quiet={!a.status} source={a.source} />
          <Fact label="Sale value" value={money(a.total_sale_value)} />
          <Fact label="Payments received" value={money(a.payments_received)} />
          <Fact label="Remaining" value={money(a.remaining_amount)} />
          <Fact label="Overdue" value={money(a.amount_overdue)} />
          <Fact label="Terms" value={a.terms || '—'} quiet={!a.terms} />
          <Fact label="Aging" value={a.aging.state === 'DERIVED'
            ? `current ${money(a.aging.current)} · 1–30 ${money(a.aging.d1_30)} · 31–60 ${money(a.aging.d31_60)} · 61–90 ${money(a.aging.d61_90)} · 90+ ${money(a.aging.d90_plus)}`
            : a.aging.why || 'Not available'} quiet={a.aging.state !== 'DERIVED'} />
        </dl>
      </div>

      <div className="um-section">
        <h3>Who pays, who decides</h3>
        <dl className="um-kv">
          <Fact label="Financial responsibility" value={rp.financial.type ? rp.financial.type.replace(/_/g, ' ').toLowerCase() : 'Not recorded'} quiet={!rp.financial.type} source={rp.financial.source} />
          <Fact label="Responsible party" value={rp.financial.name ? `${rp.financial.name}${rp.financial.phone ? ` · ${rp.financial.phone}` : ''}${rp.financial.email ? ` · ${rp.financial.email}` : ''}` : 'Not recorded'} quiet={!rp.financial.name} />
          <Fact label="Decision maker (POA)" value={rp.decision_maker.name ? `${rp.decision_maker.name}${rp.decision_maker.phone ? ` · ${rp.decision_maker.phone}` : ''}` : 'Not recorded'} quiet={!rp.decision_maker.name} source={rp.decision_maker.source} />
          <Fact label="Patient liability" value={ctx.liability.value === null ? 'Not recorded' : money(ctx.liability.value)} quiet={ctx.liability.value === null}
            source={ctx.liability.source === 'PA_ENTERED' ? `PA-entered${ctx.liability.checked_at ? `, checked ${shortDate(ctx.liability.checked_at)}` : ''}` : null} />
          {ctx.liability.conflict && <Fact label="Reconciliation" value="Needs reconciliation — a prior source carries a different amount." />}
        </dl>
      </div>

      <div className="um-section">
        <h3>Approval and UMED</h3>
        {ctx.umed ? (
          <dl className="um-kv">
            <Fact label="Enrollment" value={ctx.enrollment.state === 'ENROLLED' ? 'Enrolled' : ctx.enrollment.state === 'LEAD' ? `Lead · ${ctx.enrollment.lead_status || 'status not recorded'}` : 'No lead'} source={ctx.enrollment.source} />
            {/* Approved is the act (final verification 2026-10-06, P2-C): FLMMIS is its evidence, Monday's stage is Monday's claim. */}
            <Fact label="UMED stage" value={STAGE_LABEL[ctx.umed.stage as Stage] ? stageLabelOf(ctx.umed.stage as Stage, ctx.umed.approval.proven) : ctx.umed.stage.replace(/_/g, ' ').toLowerCase()} />
            <Fact label="Approval" value={APPROVAL_WORD_LABEL[approvalWordOf({ proven: ctx.umed.approval.proven, flmmis: ctx.umed.flmmis, stage: ctx.umed.stage, finance_state: ctx.umed.finance_state })]}
              quiet={!ctx.umed.approval.proven} source={ctx.umed.approval.proven && ctx.umed.approved_on ? shortDate(ctx.umed.approved_on) : null} />
            <Fact label="NOCA / FLMMIS" value={`${ctx.umed.noca} / ${ctx.umed.flmmis}`} />
            <Fact label="Sent to DCF" value={ctx.umed.last_native_sending ? `${shortDate(ctx.umed.last_native_sending)} (recorded in eMerge)` : ctx.umed.sent_to_dcf_on || ctx.umed.submitted_on ? `${shortDate(ctx.umed.sent_to_dcf_on || ctx.umed.submitted_on)} (board date)` : 'Not sent'} />
            {ctx.advocate && <Fact label="Patient advocate" value={ctx.advocate.name || 'Unassigned'} quiet={!ctx.advocate.name} />}
          </dl>
        ) : <p className="um-quiet">No UMED case for this resident.</p>}
      </div>

      <div className="um-section">
        <h3>Provider and next visit</h3>
        <dl className="um-kv">
          <Fact label="Last tested" value={ctx.testing.state === 'SOURCE_ASSERTED' ? `${shortDate(ctx.testing.last_tested)}${ctx.testing.tested_by ? ` by ${ctx.testing.tested_by}` : ''} (CounselEAR-reported)` : 'Not available'} quiet={ctx.testing.state !== 'SOURCE_ASSERTED'} />
          {ctx.testing.state !== 'SOURCE_ASSERTED' && <Fact label="" value={<span className="um-quiet">{ctx.testing.why}</span>} />}
          <Fact label="Projected next visit" value={ctx.next_visit.projected ? shortDate(ctx.next_visit.projected) : ctx.next_visit.tentative ? `${shortDate(ctx.next_visit.tentative)} (tentative)` : 'Not scheduled'} quiet={!ctx.next_visit.projected && !ctx.next_visit.tentative} source="read-only" />
          <Fact label="Last facility visit" value={shortDate(ctx.next_visit.last_visit)} quiet={!ctx.next_visit.last_visit} />
        </dl>
        {ctx.facility && ctx.facility.contacts.length > 0 && (
          <ul className="fn-contacts" aria-label="Facility contacts">
            {ctx.facility.contacts.slice(0, 5).map((c, i) => <li key={i}>{c.name}{c.title ? ` · ${c.title}` : ''}{c.phone ? ` · ${c.phone}` : ''}{c.email ? ` · ${c.email}` : ''}</li>)}
          </ul>
        )}
      </div>

      <div className="um-section">
        <h3>Documents and recordings</h3>
        {!ctx.documents.available ? <p className="um-quiet">{ctx.documents.why}</p>
          : (ctx.documents.items || []).length === 0 ? <p className="um-quiet">eMerge holds no document for this resident.</p>
            : <ul className="um-packets" aria-label="Resident documents">{(ctx.documents.items || []).map((d) => <DocRow key={d.id} patientId={ctx.resident.id} doc={d} />)}</ul>}
      </div>

      <div className="um-section">
        <h3>Recent history</h3>
        {ctx.notes.length > 0 && <ul className="fn-notes" aria-label="Finance notes">{ctx.notes.slice(0, 10).map((n) => <li key={n.id}><span className="um-quiet">{shortDate(n.recorded_at)} · {NOTE_KIND_LABEL[n.kind]}{n.recorded_by_name ? ` · ${n.recorded_by_name}` : ''}</span><br />{n.note}</li>)}</ul>}
        {ctx.history.length === 0 ? <p className="um-quiet">Nothing recorded yet.</p>
          : <ol className="um-story">{ctx.history.slice(0, 12).map((h, i) => <li key={i}><span className="when">{shortDate(h.at)}</span><span className="what">{h.what.replace(/_/g, ' ').toLowerCase()}{h.who ? ` · ${h.who}` : ''}{h.source === 'MONDAY' ? ' · from the board' : ''}</span></li>)}</ol>}
        {ctx.expedited_dispense.history.filter((r) => r.id !== open?.id).map((r) => <ExpeditedCard key={r.id} r={r} />)}
      </div>

      {scope.writes === 'NATIVE' && (scope.can.add_note || scope.can.request_expedited_dispense) && (
        <div className="um-section">
          <h3>Actions</h3>
          {scope.can.add_note && <NoteForm patientId={ctx.resident.id} kinds={scope.note_kinds} onDone={onChanged} />}
          {scope.can.request_expedited_dispense && !open && <ExpediteForm patientId={ctx.resident.id} urgencies={scope.urgencies} onDone={onChanged} />}
        </div>
      )}
      {scope.writes === 'NONE' && <div className="um-readonly"><strong>Read-only.</strong> Finance notes and expedited-dispense requests are not open on this process yet.</div>}
    </section>
  );
}

/**
 * Load one resident's dossier and render it. A failure is said, never shown
 * as an empty dossier; a resident outside the caller's scope is "not found".
 */
export default function ResidentDossier({ patientId, scope, onChanged }: { patientId: string; scope: FinanceScope; onChanged?: (msg: string) => void }) {
  const [ctx, setCtx] = useState<ResidentContext | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const load = useCallback(async () => {
    setErr(null);
    try {
      const r = await financeApi.resident(patientId);
      if (!r.success || !r.data) { setCtx(null); setErr(r.code === 'NOT_FOUND' ? 'No such resident here. They may be in a building outside your scope.' : outcomeOf(r)); return; }
      setCtx(r.data);
    } catch { setCtx(null); setErr('Could not reach the server.'); }
  }, [patientId]);
  useEffect(() => { setNotice(null); setCtx(null); load(); }, [load]);
  if (err) return <section className="um-card"><p className="um-notice stop" role="alert">{err} <button type="button" className="um-open" onClick={load}>Try again</button></p></section>;
  if (!ctx) return <section className="um-card"><p className="um-quiet" role="status">Loading the resident's Finance context…</p></section>;
  return <Context ctx={ctx} scope={scope} notice={notice} onChanged={async (m) => { setNotice(m); if (onChanged) onChanged(m); await load(); }} />;
}
